import { Injectable, OnDestroy } from '@angular/core';
import {
    IConvertorAsync,
    IExtendedProjectInDTO,
    IProject,
    IProjectConfigs,
    IProjectInDTO,
    IProjectOutDTO,
    Project,
} from 'src/app/models/project/project-model';
import { SceneConverterService } from './scene-converter.service';
import { EditConvertorService } from './edit/edit-convertor.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { SharedProjectDBService } from 'src/app/services/state-management/project/shared-project-indexdb.service';
import { ProfileService } from '../../show/profile.service';
import { IComposition, ONLY_AUDIO_COMPOSITIONS, ProjectRecordingTypeEnum } from '../../../models/defines';
import { FeatureService } from '../../feature-service.service';
import { v4 as uuidv4 } from 'uuid';
import { timeAgo } from '../../functions-helper.service';

@Injectable()
export class ProjectConverterService implements IConvertorAsync<IProjectInDTO, IProject, IProjectOutDTO>, OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        private sceneConverter: SceneConverterService,
        private editConvertor: EditConvertorService,
        private projectSharedDB: SharedProjectDBService,
        private userService: ProfileService,
        private featureService: FeatureService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async inToLocalAsync(
        inputFromInDTO: IProjectInDTO,
        waitForLocalVideos: boolean,
        streamId?: string
    ): Promise<IProject> {
        const input = projectInToExtend(inputFromInDTO);
        if (!input) {
            console.error(`Cannot convert input project to project dto project because it's null.`);
            return;
        }

        const dbConfig = this.projectSharedDB.getIndexDBConfigs(input.id);
        /// Initializing the project in the index db (the function is checking if it was initialized before)
        const projectIndexData = this.projectSharedDB.initializeAsync(dbConfig);
        if (waitForLocalVideos) {
            try {
                await projectIndexData.projectIndexDBData;
            } catch (error) {
                console.error(`Could not initialize db for project ${input.id}`);
            }
        }

        const baseDesignGroupPath = input.designGroup.chosenDesign.basePath;
        const localScenes = await Promise.all(
            input.scenes?.map((inScene, index) =>
                this.sceneConverter.inToLocalAsync(
                    inScene,
                    baseDesignGroupPath,
                    waitForLocalVideos,
                    input.id,
                    projectIndexData,
                    index,
                    streamId
                )
            )
        );

        const localEdits =
            input.edits?.map((edit) => {
                return this.editConvertor.inToLocal(edit, localScenes, baseDesignGroupPath, input.id);
            }) || [];

        // const isShuffllUser = await this.featureService.getFeatureAccessValue('video_in_ai_voice');
        // if (
        //     input.videoType !== ProjectRecordingTypeEnum.CAMERA &&
        //     input.videoType !== ProjectRecordingTypeEnum.AVATAR &&
        //     !isShuffllUser
        // ) {
        //     input.designGroup.chosenDesign.compositions = this.filterCompositionsToAudioOnly(
        //         input.designGroup.chosenDesign.compositions
        //     );
        // }
        const projectConfigs: IProjectConfigs = {
            id: input.id,
            branding: input.branding,
            designGroup: input.designGroup,
            toMaxine: input.toMaxine,
            statuses: input.statuses,
            user: input.user,
            updatedAt: input.updatedAt,
            title: input.title,
            scenes: localScenes ?? [],
            creative: input.creative,
            icon: input.icon,
            name: input.name,
            createdAt: input.createdAt,
            edits: localEdits,
            indexDBData: projectIndexData,
            launchAnswers: input.launchAnswers,
            videoType: input.videoType,
            invites: input.invites ?? [],
            shortTitle: input.shortTitle,
            isProjectOwner: this.userService.user$.value?.email === input.user.email,
            aiVoiceSettings: input.aiVoiceSettings,
            aiAvatarSettings: input.aiAvatarSettings,
            isPlayground: input.isPlayground,
            language: input.language,
            fonts: input.fonts,
            badgeLabel: input.badgeLabel,
            exportsCount: input.exportsCount,
            tooltip: input.tooltip,
            showExportCount: input.showExportCount,
            typeIcon: input.typeIcon,
            chosenAiVoiceId: input.chosenAiVoiceId,
            formattedTime: input.formattedTime,
        };
        const newProject = new Project(projectConfigs);
        return newProject;
    }
    filterCompositionsToAudioOnly(compositions: IComposition[]): IComposition[] {
        return compositions.filter((composition) => ONLY_AUDIO_COMPOSITIONS.includes(composition.type));
    }
    localToOut(client: IProject): IProjectOutDTO {
        if (!client) {
            console.error(`Cannot convert project to out dto project because it's null.`);
            return;
        }

        const outDTOScenes = client.scenes?.map((scene) => this.sceneConverter.localToOut(scene));

        const extendedProjectOutDTO: IProjectOutDTO = {
            id: client.id,
            user: {
                email: client.user.email,
                firstName: client.user.firstName,
                lastName: client.user.lastName,
            },
            branding: client.branding,
            designGroup: client.designGroup,
            scenes: outDTOScenes ?? [],
            toMaxine: client.toMaxine,
            statuses: client.statuses,
            creative: client.creative,
            name: client.name,
            icon: client.icon,
            title: client.title,
            createdAt: client.createdAt,
            updatedAt: client.updatedAt,
            edits: client.edits,
            launchAnswers: client.launchAnswers,
            videoType: client.videoType ?? ProjectRecordingTypeEnum.CAMERA,
            invites: client.invites,
            shortTitle: client.shortTitle,
            aiVoiceSettings: client.aiVoiceSettings,
            aiAvatarSettings: client.aiAvatarSettings,
            isPlayground: client.isPlayground,
            language: client.language,
            fonts: client.fonts,
        };

        return extendedProjectOutDTO;
    }
}

const STATUS_TOOLTIP: Record<string, string> = {
    planning: 'Project is in planning phase',
    recording: 'Project is being recorded',
    editing: 'Project is under review',
    exporting: 'Export in progress',
};

const VIDEO_TYPE_ICON: Record<string, string> = {
    [ProjectRecordingTypeEnum.AI_VOICE]: 'auto_awesome',
    [ProjectRecordingTypeEnum.AVATAR]: 'person',
    [ProjectRecordingTypeEnum.MIC]: 'mic',
};

const STATUS_LABEL: Record<string, string> = {
    editing: 'Reviewing',
};
export function projectInToExtend(proj: IProjectInDTO): IExtendedProjectInDTO {
    const status = proj.statuses?.general;
    const editsCount = proj.edits?.length ?? 0;
    const exportsCount = editsCount - 1;

    return {
        ...proj,
        iconLayout: {
            _id: uuidv4(),
            lottiePath: proj.icon,
            relativeLottiePath: proj.icon,
            baseDesignPath: '',
        },
        formattedTime: formatTime(proj),
        typeIcon: VIDEO_TYPE_ICON[proj.videoType] ?? 'videocam',
        tooltip: status === 'done' ? `${exportsCount} export(s) available` : STATUS_TOOLTIP[status] ?? '',
        badgeLabel: status === 'done' ? (editsCount > 2 ? 'Exports' : 'Export') : STATUS_LABEL[status] ?? status ?? '',
        exportsCount,
        showExportCount: status === 'done' && editsCount > 1,
    };
}
function formatTime(project: IProjectInDTO): string {
    return timeAgo(project.createdAt);
}
